if (!customElements.get('hero-benefit-section')) {
  class HeroBenefitSection extends HTMLElement {
    connectedCallback() {
      this.heroSection = this.querySelector('.hero-section');
      this.buttons = Array.from(this.querySelectorAll('.benefit-icon-item'));
      this.contentTitle = this.querySelector('[data-content-title]');
      this.contentText = this.querySelector('[data-content-text]');
      this.timerBar = this.querySelector('[data-timer-bar]');
      this.reviewBox = this.querySelector('[data-review-box]');
      this.reviewText = this.querySelector('[data-review-text]');
      this.reviewerName = this.querySelector('[data-reviewer-name]');
      this.starRating = this.querySelector('[data-star-rating]');
      this.contentDisplay = this.querySelector('.content-display');

      if (!this.heroSection || this.buttons.length === 0) return;

      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.isTimerDisabled =
        this.heroSection.getAttribute('data-disable-timer') === 'true' || this.prefersReducedMotion.matches;
      this.timerDurationSeconds = parseInt(this.heroSection.getAttribute('data-timer-duration'), 10) || 10;
      this.timerDuration = this.timerDurationSeconds * 1000;
      if (this.timerBar) {
        this.timerBar.style.setProperty('--timer-duration', String(this.timerDurationSeconds));
      }

      this.currentIndex = 0;
      this.autoTimer = null;
      this.isUserInteracting = false;

      this.buttons.forEach((button, index) => {
        button.addEventListener('click', () => {
          this.isUserInteracting = true;
          this.setActive(index);

          if (!this.isTimerDisabled) {
            setTimeout(() => {
              this.isUserInteracting = false;
              this.scheduleNext();
            }, 2000);
          }
        });
      });

      this.onResize = this.onResize.bind(this);
      window.addEventListener('resize', this.onResize);

      this.setActive(0);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      clearTimeout(this.autoTimer);
      clearTimeout(this.resizeTimeout);
    }

    getData(index) {
      return this.querySelector('.content-data div[data-index="' + index + '"]');
    }

    scheduleNext() {
      clearTimeout(this.autoTimer);
      this.autoTimer = setTimeout(() => {
        this.setActive((this.currentIndex + 1) % this.buttons.length);
      }, this.timerDuration);
    }

    setActive(index) {
      clearTimeout(this.autoTimer);
      this.autoTimer = null;

      if (this.timerBar) {
        this.timerBar.classList.remove('animate');
        this.timerBar.style.width = '0%';
        void this.timerBar.offsetWidth;
      }

      this.buttons.forEach((button, i) => {
        const icon = button.querySelector('.benefit-icon-image');
        button.setAttribute('aria-pressed', i === index ? 'true' : 'false');
        if (!icon) return;
        icon.classList.toggle('active', i === index);
        icon.classList.toggle('inactive', i !== index);
      });

      const data = this.getData(index);
      if (data) {
        const titleEl = data.querySelector('h3');
        const textEl = data.querySelector('p');
        if (this.contentTitle && titleEl) this.contentTitle.textContent = titleEl.textContent;
        if (this.contentText && textEl) this.contentText.textContent = textEl.textContent;

        this.updateIcon(data);
        this.updateReview(data);
        this.updateBlockImage(data);
      }

      this.currentIndex = index;

      if (!this.isTimerDisabled && this.timerBar) {
        setTimeout(() => {
          this.timerBar.classList.add('animate');
        }, 50);
      }

      if (!this.isUserInteracting && !this.isTimerDisabled) {
        this.scheduleNext();
      }
    }

    updateIcon(data) {
      const oldIcon = this.querySelector('[data-content-icon]');
      if (!oldIcon) return;

      const iconType = data.getAttribute('data-icon-type');
      const iconSrc = data.getAttribute('data-icon');
      const iconSvg = data.getAttribute('data-icon-svg');
      let newIcon;

      if (iconType === 'svg') {
        if (oldIcon.classList.contains('content-icon-svg')) {
          oldIcon.innerHTML = iconSvg || '';
        } else {
          newIcon = document.createElement('span');
          newIcon.className = 'content-icon content-icon-svg';
          newIcon.setAttribute('data-content-icon', '');
          newIcon.setAttribute('aria-hidden', 'true');
          newIcon.innerHTML = iconSvg || '';
          oldIcon.replaceWith(newIcon);
        }
      } else if (iconSrc) {
        if (oldIcon.tagName === 'IMG') {
          oldIcon.src = iconSrc;
        } else {
          newIcon = document.createElement('img');
          newIcon.className = 'content-icon';
          newIcon.setAttribute('data-content-icon', '');
          newIcon.src = iconSrc;
          newIcon.alt = '';
          newIcon.width = 80;
          newIcon.height = 80;
          oldIcon.replaceWith(newIcon);
        }
      }
    }

    updateReview(data) {
      const hideReviewBox = data.getAttribute('data-hide-review-box') === 'true';
      if (this.reviewBox) {
        this.reviewBox.style.display = hideReviewBox ? 'none' : 'flex';
      }
      if (this.reviewerName) {
        this.reviewerName.textContent = data.getAttribute('data-reviewer-name') || '';
      }
      if (this.reviewText) {
        this.reviewText.textContent = data.getAttribute('data-review') || '';
      }
      if (this.starRating) {
        const stars = parseInt(data.getAttribute('data-stars'), 10) || 0;
        this.starRating.textContent = '';
        for (let i = 0; i < 5; i++) {
          const star = document.createElement('span');
          star.className = 'hero-benefit-star';
          star.setAttribute('aria-hidden', 'true');
          star.textContent = i < stars ? '★' : '☆';
          this.starRating.appendChild(star);
        }
      }
    }

    updateBlockImage(data) {
      let wrapper = this.querySelector('[data-block-image-wrapper]');
      const src = data.getAttribute('data-block-image');

      if (src && src !== 'null' && src.indexOf('no-image') === -1) {
        if (!wrapper) {
          wrapper = document.createElement('div');
          wrapper.className = 'block-image-wrapper';
          wrapper.setAttribute('data-block-image-wrapper', '');
          const img = document.createElement('img');
          img.className = 'block-image';
          img.setAttribute('data-block-image', '');
          img.alt = '';
          img.loading = 'lazy';
          wrapper.appendChild(img);
          if (this.contentDisplay) this.contentDisplay.appendChild(wrapper);
        }

        const image = wrapper.querySelector('[data-block-image]');
        if (image) {
          image.src = src;
          image.style.borderRadius = (data.getAttribute('data-block-image-border-radius') || 0) + 'px';
          image.style.border =
            (data.getAttribute('data-block-image-border-width') || 0) +
            'px solid ' +
            (data.getAttribute('data-block-image-border-color') || 'transparent');

          const aspect = data.getAttribute('data-block-image-aspect');
          if (aspect === 'custom') {
            const isMobile = window.innerWidth < 750;
            const height = isMobile
              ? data.getAttribute('data-block-image-height-mobile')
              : data.getAttribute('data-block-image-height-desktop');
            image.style.height = height + 'px';
            image.style.aspectRatio = 'auto';
          } else {
            image.style.aspectRatio = aspect || '16/9';
            image.style.height = 'auto';
          }
        }

        wrapper.style.display = 'block';
        const orderMap = { above_title: '1', under_title: '3', above_review: '5', under_review: '7' };
        const position = data.getAttribute('data-block-image-position');
        if (position && orderMap[position]) {
          wrapper.style.order = orderMap[position];
        }
      } else if (wrapper) {
        wrapper.style.display = 'none';
      }
    }

    onResize() {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        const data = this.getData(this.currentIndex);
        if (!data) return;
        const image = this.querySelector('[data-block-image]');
        if (image && data.getAttribute('data-block-image-aspect') === 'custom') {
          const isMobile = window.innerWidth < 750;
          const height = isMobile
            ? data.getAttribute('data-block-image-height-mobile')
            : data.getAttribute('data-block-image-height-desktop');
          image.style.height = height + 'px';
        }
      }, 100);
    }
  }

  customElements.define('hero-benefit-section', HeroBenefitSection);
}
